import { GlobalActions } from '@fcom/core/actions';
import { DataUtils, setIn } from '@fcom/core/utils';
import { GlobalBookingActions } from '@fcom/common/store';
import { SortBy } from '@fcom/common-booking';
import { defaultFilterValues } from '@fcom/common-booking/interfaces/flight-filters.interface';
import { AirOffersStatus } from '@fcom/common/interfaces/booking/air-offers-state.interface';
import { AirBoundsState } from '@fcom/common/interfaces/booking/air-bounds-state.interface';

import { BookingActions, BoundsActions } from '../actions';

export const initialState: AirBoundsState = Object.seal({
  status: AirOffersStatus.INITIAL,
  outbounds: undefined,
  inbounds: undefined,
  selectedOutboundAirBoundId: undefined,
  selectedInboundAirBoundId: undefined,
  sortBy: SortBy.DURATION,
  filters: {
    stopsCount: defaultFilterValues.defaultStopsOption,
    refundable: defaultFilterValues.defaultRefundabilityOption,
    cabin: defaultFilterValues.defaultTravelClass,
    arrivalTime: [...defaultFilterValues.defaultTimeOptions],
    departureTime: [...defaultFilterValues.defaultTimeOptions],
    maxStops: defaultFilterValues.defaultStopsOption,
  },
  nextFilters: {
    stopsCount: defaultFilterValues.defaultStopsOption,
    refundable: defaultFilterValues.defaultRefundabilityOption,
    cabin: defaultFilterValues.defaultTravelClass,
    arrivalTime: [...defaultFilterValues.defaultTimeOptions],
    departureTime: [...defaultFilterValues.defaultTimeOptions],
    maxStops: defaultFilterValues.defaultStopsOption,
  },
  outboundCache: {},
});

type BoundActionsUnionType =
  | BoundsActions.ActionsUnion
  | GlobalActions.ActionsUnion
  | BookingActions.ActionsUnion
  | GlobalBookingActions.GlobalBookingActionsUnion;

// eslint-disable-next-line complexity
export function boundsReducer(state = initialState, action: BoundActionsUnionType): AirBoundsState {
  /* eslint-disable no-fallthrough */
  switch (action.type) {
    case BoundsActions.startLoadingOutbounds.type:
      return DataUtils.wrap(initialState)
        .setIn('status', AirOffersStatus.PENDING)
        .setIn('sortBy', state.sortBy)
        .setIn('filters', state.filters)
        .setIn('outboundCache', state.outboundCache)
        .value();
    case BoundsActions.startLoadingInbounds.type:
      return DataUtils.wrap(state)
        .setIn('inbounds', undefined)
        .setIn('selectedInboundAirBoundId', undefined)
        .setIn('status', AirOffersStatus.PENDING)
        .value();
    case BoundsActions.success.type:
      return setIn(state, 'status', AirOffersStatus.OK);
    case BoundsActions.error.type:
      return DataUtils.wrap(initialState).setIn('status', AirOffersStatus.ERROR).value();
    case BoundsActions.invalidInput.type:
      return DataUtils.wrap(initialState).setIn('status', AirOffersStatus.INVALID_INPUT).value();
    case BoundsActions.noFlightsFound.type:
      return DataUtils.wrap(initialState).setIn('status', AirOffersStatus.NO_FLIGHTS_FOUND).value();

    case BoundsActions.setOutbounds.type:
      return DataUtils.wrap(state).setIn('outbounds', action.bounds).setIn('status', AirOffersStatus.OK).value();

    case BoundsActions.setInbounds.type:
      return DataUtils.wrap(state).setIn('inbounds', action.bounds).setIn('status', AirOffersStatus.OK).value();

    case BoundsActions.setLastRequestParams.type:
      return setIn(state, 'lastRequestParams', action.params);

    case BoundsActions.setOutboundAirBoundId.type: {
      const selectedInboundAirBoundId =
        state.selectedOutboundAirBoundId === action.outboundAirBoundId ? state.selectedInboundAirBoundId : undefined;

      const isSelectedCheapestOffer =
        state.outbounds?.boundGroups.some(
          (boundGroup) =>
            boundGroup.fareFamilies.find((fareFamily) => fareFamily.id === action.outboundAirBoundId)?.isCheapestOffer
        ) ?? false;

      return {
        ...state,
        selectedOutboundAirBoundId: action.outboundAirBoundId,
        selectedInboundAirBoundId,
        isSelectedCheapestOffer,
      };
    }

    case BoundsActions.setInboundAirBoundId.type:
      return setIn(state, 'selectedInboundAirBoundId', action.inboundAirBoundId);

    case BoundsActions.setSortBy.type:
      return setIn(state, 'sortBy', action.sortBy);
    case BoundsActions.setArrivalTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          arrivalTime: action.arrivalTime,
        },
      };
    case BoundsActions.resetArrivalTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          arrivalTime: defaultFilterValues.defaultTimeOptions,
        },
      };
    case BoundsActions.setDepartureTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          departureTime: action.departureTime,
        },
      };
    case BoundsActions.resetDepartureTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          departureTime: defaultFilterValues.defaultTimeOptions,
        },
      };
    case BoundsActions.setNextArrivalTime.type:
      return {
        ...state,
        nextFilters: {
          ...state.nextFilters,
          arrivalTime: action.arrivalTime,
        },
      };
    case BoundsActions.resetNextArrivalTime.type:
      return {
        ...state,
        nextFilters: {
          ...state.nextFilters,
          arrivalTime: defaultFilterValues.defaultTimeOptions,
        },
      };
    case BoundsActions.setNextDepartureTime.type:
      return {
        ...state,
        nextFilters: {
          ...state.nextFilters,
          departureTime: action.departureTime,
        },
      };
    case BoundsActions.resetNextDepartureTime.type:
      return {
        ...state,
        nextFilters: {
          ...state.nextFilters,
          departureTime: defaultFilterValues.defaultTimeOptions,
        },
      };
    case BoundsActions.setRefundable.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          refundable: action.refundable,
        },
      };
    case BoundsActions.resetRefundable.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          refundable: defaultFilterValues.defaultRefundabilityOption,
        },
      };
    case BoundsActions.setStopsCount.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          stopsCount: action.stopsCount,
        },
      };

    case BoundsActions.resetStopsCount.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          stopsCount: defaultFilterValues.defaultStopsOption,
        },
      };
    case BoundsActions.setMaxStops.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          maxStops: action.maxStops,
        },
      };

    case BoundsActions.resetMaxStops.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          maxStops: defaultFilterValues.defaultStopsOption,
        },
      };
    case BoundsActions.setCabin.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          cabin: action.cabin,
        },
      };
    case BoundsActions.resetCabin.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          cabin: action.cabin || defaultFilterValues.defaultTravelClass,
        },
      };
    case BoundsActions.resetAllFilters.type: {
      return {
        ...state,
        filters: {
          stopsCount: defaultFilterValues.defaultStopsOption,
          refundable: defaultFilterValues.defaultRefundabilityOption,
          cabin: action.keepCabin ? state.filters.cabin : action.defaultCabin || defaultFilterValues.defaultTravelClass,
          arrivalTime: [...defaultFilterValues.defaultTimeOptions],
          departureTime: [...defaultFilterValues.defaultTimeOptions],
          maxStops: defaultFilterValues.defaultStopsOption,
        },
      };
    }
    case BoundsActions.resetAllNextFilters.type: {
      return {
        ...state,
        nextFilters: {
          stopsCount: state.filters.stopsCount,
          maxStops: state.filters.maxStops,
          refundable: state.filters.refundable,
          cabin: state.filters.cabin,
          arrivalTime: state.filters.arrivalTime,
          departureTime: state.filters.departureTime,
        },
      };
    }
    case BoundsActions.setAllNextFilters.type:
      return {
        ...state,
        nextFilters: {
          ...state.nextFilters,
          ...action.nextFilters,
        },
      };
    case BoundsActions.reset.type:
    case BookingActions.setTravelType.type: // @todo Do we need to fix this, as we moved setTravelType from the booking to the globalBooking store?
    case BookingActions.selectTravelClass.type: // @todo Do we need to fix this, as we moved selectTravelClass from the booking to the globalBooking store?
    // Clean up bounds when order created
    case GlobalActions.confirmation.type:
      return setIn({ ...initialState, lastRequestParams: state.lastRequestParams }, 'sortBy', state.sortBy);
    case BoundsActions.setOutboundCache.type:
      return {
        ...state,
        outboundCache: {
          ...state.outboundCache,
          [action.hash]: {
            createdAt: action.createdAt,
            results: action.bounds,
          },
        },
      };
    case BoundsActions.resetOutboundCache.type:
      return {
        ...state,
        outboundCache: {},
      };
    default:
      return state;
  }
  /* eslint-enable no-fallthrough */
}
